// src/screens/SavingsRateTable.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 43 Section 1 — Savings Rate Table
// Shows what different savings rates produce at each age band.
// Contextualises the 15%+ target before Order of Operations.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import { ClientData } from '../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const AGE_BANDS = [20, 25, 30, 35, 40, 45, 50, 55, 60];
const SAVINGS_RATES = [10, 15, 20, 25, 30, 35, 40];
const DRAWDOWN_RATE = 0.04;

function calcIncomeReplaced(age: number, savingsRate: number, growthRate: number, retireAge: number): number {
  const years = Math.max(0, retireAge - age);
  if (years <= 0) return 0;
  const n = years * 12;
  const monthlyRate = growthRate / 12;
  const monthlyContribution = savingsRate / 100 / 12;
  const fv = monthlyRate > 0
    ? monthlyContribution * ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate)
    : monthlyContribution * n;
  const annualDrawdown = fv * DRAWDOWN_RATE;
  return Math.round(annualDrawdown * 100); // as % of annual £1 income
}

function cellColour(value: number): { bg: string; text: string } {
  if (value < 80) return { bg: '#FEE2E2', text: '#991B1B' };
  if (value < 100) return { bg: '#FEF3C7', text: '#92400E' };
  return { bg: '#D1FAE5', text: '#065F46' };
}

export default function SavingsRateTable({ data }: Props) {
  const age = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge;
  const growthRate = data.assumptionPensionGrowth ?? 0.08;
  const growthPct = Math.round(growthRate * 100);

  // Current savings rate — monthly savings / monthly gross income
  const monthlyGross = ((data.salary || 0) + (data.dividends || 0) + (data.rentalIncomeNet ?? 0)) / 12;
  const monthlyInvested = (data.pensionCompanyFunded ?? 0) + (data.pensionMonthly ?? 0) + (data.isaContribution ?? 0) + (data.efTopUp ?? 0);
  const currentSavingsRate = monthlyGross > 0
    ? Math.round((monthlyInvested / monthlyGross) * 100)
    : 0;
  const highlightCol = currentSavingsRate > 0
    ? SAVINGS_RATES.reduce((best, r) => Math.abs(r - currentSavingsRate) < Math.abs(best - currentSavingsRate) ? r : best, SAVINGS_RATES[0])
    : null;

  // Find client's nearest age row
  const nearestAge = AGE_BANDS.reduce((best, a) => Math.abs(a - age) < Math.abs(best - age) ? a : best, AGE_BANDS[0]);

  // Pre-compute table
  const tableData = useMemo(() => {
    return AGE_BANDS.map(a => ({
      age: a,
      cells: SAVINGS_RATES.map(r => calcIncomeReplaced(a, r, growthRate, freedomAge)),
    }));
  }, [growthRate, freedomAge]);

  // Client's exact position — use actual age and rate, not snapped to table cell
  const clientExactReplacement = currentSavingsRate > 0
    ? calcIncomeReplaced(age, currentSavingsRate, growthRate, freedomAge)
    : null;

  // ── Edge case: age >= 60 ─────────────────────────────────────────────────
  if (age >= freedomAge - 5) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8 flex items-center justify-center">
        <div className="max-w-2xl text-center">
          <h1 className="text-2xl font-bold text-ma-navy mb-4">How Savings Rate Shapes Your Future</h1>
          <p className="text-gray-600">
            Savings rate table applies to those more than 5 years from their target freedom age.
            Your Freedom Number calculation is personalised — see above.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
      <div className="max-w-5xl mx-auto">

        {/* ── Title ──────────────────────────────────────────────────────── */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-black text-ma-navy mb-2">How Savings Rate Shapes Your Future</h1>
          <p className="text-sm text-gray-500">
            At {growthPct}% annual growth — what % of your income could you replace by {freedomAge}?
          </p>
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        <div className="overflow-x-auto mb-8">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide p-2 border-b border-gray-200">
                  Age
                </th>
                {SAVINGS_RATES.map(r => (
                  <th
                    key={r}
                    className="text-center text-xs font-semibold uppercase tracking-wide p-2 border-b border-gray-200"
                    style={{
                      color: r === highlightCol ? '#0D9488' : '#6B7280',
                      backgroundColor: r === highlightCol ? '#F0FDFA' : undefined,
                    }}
                  >
                    {r}%
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => {
                const isClientRow = row.age === nearestAge;
                return (
                  <tr
                    key={row.age}
                    style={isClientRow ? { borderLeft: '3px solid #0D9488' } : undefined}
                  >
                    <td
                      className="p-2 text-xs border-b border-gray-100"
                      style={{ fontWeight: isClientRow ? 700 : 400, color: isClientRow ? '#0F172A' : '#6B7280' }}
                    >
                      {row.age}
                    </td>
                    {row.cells.map((val, ci) => {
                      const { bg, text } = cellColour(val);
                      const isHighlightCol = SAVINGS_RATES[ci] === highlightCol;
                      return (
                        <td
                          key={ci}
                          className="text-center text-xs font-semibold p-2 border-b border-gray-100"
                          style={{
                            backgroundColor: isHighlightCol ? `color-mix(in srgb, ${bg} 80%, #F0FDFA 20%)` : bg,
                            color: text,
                          }}
                        >
                          {val}%
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Callouts ───────────────────────────────────────────────────── */}
        <div className="space-y-4 mb-8">

          {/* Client's position */}
          <div className="rounded-xl p-5" style={{ backgroundColor: '#F0FDFA', borderLeft: '4px solid #0D9488' }}>
            {currentSavingsRate > 0 && clientExactReplacement !== null ? (
              <p className="text-sm text-gray-700">
                At age <strong>{age}</strong>, saving <strong>{currentSavingsRate}%</strong>, you're on track to replace approximately{' '}
                <strong className="text-teal-700">{clientExactReplacement}%</strong> of your income by {freedomAge}.
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                Enter your pension contributions in Setup to see your position.
              </p>
            )}
          </div>

          {/* 15% target */}
          {(() => {
            const rate15pct = calcIncomeReplaced(age, 15, growthRate, freedomAge);
            return (
              <div className="rounded-xl p-5" style={{ backgroundColor: '#0F172A' }}>
                <p className="text-sm text-white">
                  The standard 15% rule only gets you {rate15pct}% income replacement by {freedomAge}. FFM calculates your actual target — the specific monthly figure that reaches your Freedom Number. That number is in your plan.
                </p>
              </div>
            );
          })()}
        </div>

        {/* ── Footnote ───────────────────────────────────────────────────── */}
        <p className="text-xs text-gray-400 text-center">
          Assumes {growthPct}% annual growth, 4% drawdown rate, retirement at {freedomAge}. For illustration only.
        </p>

      </div>
    </div>
  );
}
